// workoutImport.js — paste Steve's WhatsApp text → startable workout
// pre-populated with progressive-overload-aware weight/rep targets.
//
// Pipeline:
//   1. parseWorkoutText(text)   — Gemini call. Pure structural parse;
//                                  extracts name / circuits / blocks /
//                                  exercises (block + name + reps +
//                                  isHeavy hint + Steve's prescribed
//                                  weight if any). Local, deterministic
//                                  algorithm fills numeric details
//                                  after — we never delegate weight
//                                  prescription to the model because
//                                  numerical reliability matters and
//                                  the history lives locally anyway.
//   2. populateWorkout(parsed, history, readiness, profile)
//                                — for every parsed exercise, resolves
//                                  canonical id, finds last session,
//                                  applies progressive overload sized
//                                  by readiness, and outputs a routine
//                                  shaped exactly like the existing
//                                  RIYADAH_TEMPLATES so the standard
//                                  start-workout flow picks it up
//                                  unchanged.
//
// The two are separate so the UI can show a readiness-aware preview
// before commit — and so we can re-run populate() if the user edits a
// rep target or swaps readiness intent.

import { callGemini } from "../geminiAI";
import { MODELS } from "./aiConfig";
import {
  resolveExerciseId, resolveExerciseName, resolveExerciseEntry,
  learnExercise,
} from "../exerciseCanonical";
import { newRoutineId } from "../riyadahData";
import { muscleReadinessClass } from "./muscleReadiness";

// ── Gemini schema ────────────────────────────────────────────────────
//
// Strict structural extraction. Steve's WhatsApp style is consistent
// (emojis, block letters, "X reps" suffix, occasional weight hints)
// but we don't bake his exact format into the prompt — Gemini handles
// minor variations naturally. The schema enforces shape; the prompt
// teaches priorities.

const WORKOUT_PARSE_SCHEMA = {
  type: "object",
  properties: {
    name: { type: "string" },
    circuits: { type: "integer" },
    blocks: {
      type: "array",
      items: {
        type: "object",
        properties: {
          label: { type: "string" },
          title: { type: "string" },
          exercises: {
            type: "array",
            items: {
              type: "object",
              properties: {
                name: { type: "string" },
                reps: { type: "integer" },
                isHeavy: { type: "boolean" },
                prescribedWeight: { type: "number" },
                notes: { type: "string" },
              },
              required: ["name", "reps"],
            },
          },
        },
        required: ["label", "exercises"],
      },
    },
    warmup: {
      type: "object",
      properties: {
        name: { type: "string" },
        durationMin: { type: "integer" },
        incline: { type: "number" },
        speed: { type: "number" },
      },
    },
    finisher: { type: "string" },
  },
  required: ["name", "circuits", "blocks"],
};

const WORKOUT_PARSE_PROMPT = `You are parsing a personal trainer's workout instructions copied from WhatsApp into a structured JSON routine.

EXTRACT:
- name: short workout title (e.g. "Full Body", "Push Day", "Heavy Legs"). If absent, infer from content.
- circuits: how many rounds of the blocks (typically 3). Default to 3 when unclear.
- blocks[]: each block has a label (A / B / C / D or 1 / 2 / 3 if numbered) and an exercises[] list. Each exercise has:
    - name: the exercise as written (do NOT canonicalise — the app does that downstream).
    - reps: target reps per set. Take the LOWER bound if a range (e.g. "10-12 reps" → 10). For step-based ("20 steps") use the step count. For time-based ("30 sec hold") use 30.
    - isHeavy: true when the source explicitly says "Heavy", "Top set", "Max effort", "PR attempt", or marks it as the main compound (12 reps with explicit weight pressure typically). Default false.
    - prescribedWeight: number in kg ONLY if the source explicitly states a weight (e.g. "Leg Press 90kg x 12"). Omit otherwise — the app will prescribe based on history.
    - notes: anything unusual (tempo, pause, drop set, RPE) — short.
- warmup: cardio warm-up if mentioned. Extract durationMin / incline / speed when given. Omit if no warm-up.
- finisher: optional closing cardio / conditioning, as free text.

Always return valid JSON matching the schema. Never include markdown fences or commentary.`;

// ── Phase 1: parse text → structured workout ────────────────────────

export async function parseWorkoutText(text) {
  if (!text || typeof text !== "string") throw new Error("Empty workout text");
  const raw = await callGemini(text, {
    systemInstruction: WORKOUT_PARSE_PROMPT,
    responseSchema: WORKOUT_PARSE_SCHEMA,
    model: MODELS.workoutGen || "gemini-2.5-flash",
    maxOutputTokens: 4096,
    temperature: 0.2,
    feature: "workoutImport",
  });
  let parsed;
  try { parsed = JSON.parse(raw); }
  catch { throw new Error("Couldn't parse workout — try rephrasing the message"); }
  // Defensive normalisation.
  parsed.name     = String(parsed.name || "Workout").slice(0, 60);
  parsed.circuits = Math.max(1, Math.min(5, parseInt(parsed.circuits, 10) || 3));
  if (!Array.isArray(parsed.blocks)) parsed.blocks = [];
  return parsed;
}

// ── Phase 2: progressive-overload prescription ──────────────────────
//
// The app prescribes weights × reps from YOUR history + readiness —
// Steve's hints are ignored. He doesn't see your log, the app does.
// This is double-progression with auto-regulation, the most well-
// supported PO model in the hypertrophy literature (Schoenfeld 2017+,
// Helms RP/RPE, Renaissance Periodization volume framework).
//
// Decision tree per exercise (highest priority first):
//
//   1. REST readiness                  → skip the exercise.
//   2. No history (first ever log)     → BASELINE. Library default or
//                                        0 (bodyweight). User explores
//                                        a working weight at 2 RIR.
//   3. EASY readiness                  → DELOAD. −10% from last, drop
//                                        reps to the floor. Focus on
//                                        bar speed + form.
//   4. Sleep < 5h or HRV < −15% trend  → AUTO-DELOAD even on MEDIUM.
//                                        Recovery dictates load.
//   5. Same muscle trained yesterday   → −5% from planned (acute
//                                        fatigue — second muscle exposure
//                                        too soon).
//   6. Missed rep target last session  → EARN_IT. Hold weight; reps
//                                        target = Steve's prescribed
//                                        number. Re-attempt the misser.
//   7. Hit at or above the rep ceiling → WEIGHT_UP. Bump weight by the
//                                        smallest meaningful increment;
//                                        reps drop to Steve's target
//                                        (rep window resets).
//   8. Hit target with reps in buffer  → REPS_UP. Same weight, push for
//                                        +1 rep over last session. The
//                                        double-progression engine —
//                                        accumulate reps, then convert
//                                        to weight when ceiling hits.
//   9. Hit target on a HARD day        → REPS_UP (push) or WEIGHT_UP
//                                        if the previous session was
//                                        2+ reps over target (clearly
//                                        ready for the jump).
//
// The "rep ceiling" depends on whether it's a heavy compound or
// accessory and on Steve's prescribed reps. We treat his number as the
// TOP of the rep range — when you exceed it, you've earned the bump.
//   Heavy compound (≤12 reps prescribed)   ceiling = prescribed + 2
//   Accessory (>12 reps prescribed)        ceiling = prescribed + 4
// The bump itself:
//   Heavy compound: +2.5 kg
//   Accessory:      +1.0 kg
// (Small increments matter — Schoenfeld & Krieger studies show 1-rep
// or 1-kg progression per session is enough for an intermediate.)

function roundToPlate(w) {
  return Math.round(w * 2) / 2;
}

// Heuristic — heavy if Steve marked it explicitly OR the prescribed
// reps are ≤12 (the classic compound rep range).
function classifyHeavy(ex) {
  if (ex.isHeavy === true) return true;
  return (parseInt(ex.reps, 10) || 99) <= 12;
}

function suggestForExercise(ex, getHistory, readiness, readinessInputs) {
  const canonId    = resolveExerciseId(ex.name, ex.id);
  const canonName  = resolveExerciseName(ex.name, ex.id);
  const libEntry   = resolveExerciseEntry(ex.name, ex.id);
  const history    = getHistory ? getHistory(canonId) : null;
  const sessions   = (history?.sessions || []).slice().sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
  const stevReps   = Math.max(1, parseInt(ex.reps, 10) || 10);
  const isHeavy    = classifyHeavy(ex);
  const repCeiling = stevReps + (isHeavy ? 2 : 4);
  const bumpKg     = isHeavy ? 2.5 : 1.0;
  const cls        = String(readiness || "MEDIUM").toUpperCase();

  // Helper for the return shape so every branch stays consistent.
  const out = (overrides) => ({
    canonId, canonName, libEntry,
    weight: 0, reps: stevReps,
    progression: "MAINTAIN",
    reason: "",
    warnings: [],
    isPRAttempt: false,
    isBaseline: false,
    skip: false,
    ...overrides,
  });

  // 1. REST — render the exercise as a heavy deload (no skip). The
  //    user still sees what the workout would have been so they can
  //    consciously decide to rest, do mobility, or override and lift
  //    light. Skipping silently removed every row → empty preview.
  if (cls === "REST") {
    // Need history for the deload-from baseline; if none, library default.
    const last = sessions[0];
    const baseW = last?.bestWeight ?? libEntry?.defaults?.weight ?? 0;
    return out({
      weight: roundToPlate(baseW * 0.6),
      reps: Math.max(Math.floor(stevReps * 0.6), isHeavy ? 5 : 8),
      progression: "REST",
      reason: `REST day — recommended skip. If you train, hold at ~60% × fewer reps.`,
    });
  }

  // 2. BASELINE — no history for this canonical exercise.
  if (sessions.length === 0) {
    const def = libEntry?.defaults?.weight;
    return out({
      weight: Number.isFinite(def) ? roundToPlate(def) : 0,
      progression: "BASELINE",
      isBaseline: true,
      reason: `First time logging this lift. Aim for ${stevReps} reps at 2 RIR — adjust live.`,
    });
  }

  const last   = sessions[0];
  const lastW  = last.bestWeight || 0;
  const lastR  = last.reps || stevReps;
  // The base weight we project from. If last session was at a wildly
  // different rep target, translate via Epley 1RM so relative intensity
  // stays consistent across rep schemes.
  let baseW = lastW;
  if (Math.abs(lastR - stevReps) >= 3 && lastW > 0) {
    const e1rm = lastW * (1 + lastR / 30);
    baseW = e1rm / (1 + stevReps / 30);
  }
  baseW = roundToPlate(baseW);
  const warnings = [];

  // Recovery overrides — fatigue signals from readinessInputs that
  // should knock load down even when readiness classification is MEDIUM.
  const sleepH        = readinessInputs?.sleep?.totalHours ?? null;
  const hrvDeltaPct   = readinessInputs?.hrv?.deltaPct ?? null;
  const hrvTrend3v14  = readinessInputs?.hrvTrend3vs14 ?? null;
  // muscleRecency: { Chest: 1, Back: 3, Legs: 2 } — days since each
  // muscle was hit. Build a "trained yesterday" signal for the primary.
  const primary       = (libEntry?.primary || "").trim();
  const muscleRec     = readinessInputs?.training?.muscleRecency || {};
  const primaryDays   = primary ? muscleRec[primary] : null;
  // Per-muscle readiness: the systemic verdict scoped down by how recently
  // THIS muscle was trained. A muscle hit yesterday reads EASY even on a
  // globally HARD day — so the load it gets matches its own recovery.
  const muscleCls     = Number.isFinite(primaryDays) ? muscleReadinessClass(cls, primaryDays) : cls;
  const sameMuscleY   = Number.isFinite(primaryDays) && primaryDays <= 1;

  if (Number.isFinite(sleepH) && sleepH < 5)      warnings.push(`Sleep ${sleepH.toFixed(1)}h — load reduced`);
  if (Number.isFinite(hrvTrend3v14) && hrvTrend3v14 < -15) warnings.push(`HRV trend ${hrvTrend3v14.toFixed(0)}% — autonomic stress`);
  else if (Number.isFinite(hrvDeltaPct) && hrvDeltaPct < -15) warnings.push(`HRV ${hrvDeltaPct.toFixed(0)}% vs baseline — caution`);
  if (sameMuscleY)                                 warnings.push(`${primary} hit yesterday — fresher muscles next time`);
  // Heavy compound on a muscle 2 days out: largely recovered, but flag it
  // so a back-to-back heavy block on the same region is a conscious choice.
  else if (isHeavy && primaryDays === 2)           warnings.push(`${primary} 2d out (${muscleCls}) — most fibres recovered`);

  // 3. EASY — explicit deload.
  if (cls === "EASY") {
    return out({
      weight: roundToPlate(baseW * 0.9),
      reps: Math.max(stevReps - 2, isHeavy ? 5 : 10),
      progression: "DELOAD",
      reason: `EASY day — 10% lighter from ${baseW}kg. Focus on bar speed + clean reps.`,
      warnings,
    });
  }

  // 4. Auto-deload on severe recovery deficit even when readiness was
  //    classified MEDIUM (the classifier averages; specific signals can
  //    still warrant load drop).
  const severeRecovery = (Number.isFinite(sleepH) && sleepH < 5) ||
                         (Number.isFinite(hrvTrend3v14) && hrvTrend3v14 < -15);
  if (severeRecovery) {
    return out({
      weight: roundToPlate(baseW * 0.92),
      reps: stevReps,
      progression: "DELOAD",
      reason: `Recovery markers low. Held ~8% lighter than ${baseW}kg — same reps.`,
      warnings,
    });
  }

  // 5. Recently-trained muscle → small load reduction.
  if (sameMuscleY) {
    return out({
      weight: roundToPlate(baseW * 0.95),
      reps: stevReps,
      progression: "MAINTAIN",
      reason: `${primary} trained yesterday — ~5% lighter from ${baseW}kg.`,
      warnings,
    });
  }

  // 6. Missed rep target last session → EARN_IT. Hold weight.
  if (lastR < stevReps) {
    return out({
      weight: lastW,
      reps: stevReps,
      progression: "EARN_IT",
      reason: `Hold ${lastW}kg — last session ${lastR}/${stevReps}. Complete the reps before progressing.`,
      warnings,
    });
  }

  // 7. Smashed the ceiling — bump weight, reset reps to Steve's target.
  if (lastR >= repCeiling) {
    const newW = roundToPlate(lastW + bumpKg);
    return out({
      weight: newW,
      reps: stevReps,
      progression: "WEIGHT_UP",
      isPRAttempt: true,
      reason: `+${bumpKg}kg from ${lastW}×${lastR}. Rep window resets to ${stevReps}.`,
      warnings,
    });
  }

  // 8. HARD day + previously well above target → bump weight early.
  if (cls === "HARD" && lastR >= stevReps + 2) {
    const newW = roundToPlate(lastW + bumpKg);
    return out({
      weight: newW,
      reps: stevReps,
      progression: "WEIGHT_UP",
      isPRAttempt: true,
      reason: `HARD day + last ${lastW}×${lastR}. +${bumpKg}kg — you've earned the jump.`,
      warnings,
    });
  }

  // 9. Default — REPS_UP. Same weight, push +1 rep vs last session.
  const repsTarget = Math.min(repCeiling, lastR + 1);
  return out({
    weight: lastW,
    reps: repsTarget,
    progression: "REPS_UP",
    reason: `Same ${lastW}kg — push ${repsTarget} reps (last ${lastR}). At ${repCeiling} you earn +${bumpKg}kg.`,
    warnings,
  });
}

// ── Phase 3: assemble a routine ready to start ──────────────────────

export function populateWorkout(parsed, getHistory, readiness, readinessInputs) {
  const blocks = Array.isArray(parsed?.blocks) ? parsed.blocks : [];
  const exercises_list = [];
  const blockLabels = {};
  const suggestions = []; // parallel array used by the preview UI

  for (const blk of blocks) {
    const label = String(blk.label || "A").toUpperCase().slice(0, 3);
    if (blk.title) blockLabels[label] = blk.title;
    for (const rawEx of (blk.exercises || [])) {
      const s = suggestForExercise(rawEx, getHistory, readiness, readinessInputs);
      // Skip exercises the algorithm flagged out (REST day).
      if (s.skip) continue;
      // Auto-learn unknown exercises so the canonical id sticks for
      // future workouts logged with the same name.
      if (s.libEntry == null) {
        learnExercise({
          name: s.canonName,
          id: s.canonId,
          notes: rawEx.notes,
        });
      }
      exercises_list.push({
        block: label,
        id: s.canonId,
        name: s.canonName,
        type: s.libEntry?.type || (s.libEntry?.primary === "Legs" ? "legs" : "push"),
        muscles: s.libEntry?.muscles || s.libEntry?.primary || null,
        defaultReps: s.reps,
        defaultWeight: s.weight,
      });
      suggestions.push({
        block: label,
        canonId: s.canonId,
        canonName: s.canonName,
        reps: s.reps,
        weight: s.weight,
        reason: s.reason,
        warnings: s.warnings || [],
        progression: s.progression,
        isPRAttempt: s.isPRAttempt,
        isBaseline: s.isBaseline,
      });
    }
  }

  // Warm-up cardio — matches the routine schema's `cardio` field.
  const cardio = parsed?.warmup?.name ? {
    name:     parsed.warmup.name,
    duration: parsed.warmup.durationMin || 10,
    incline:  parsed.warmup.incline,
    speed:    parsed.warmup.speed,
  } : null;

  const routine = {
    id: newRoutineId(),
    name: parsed.name || "Imported workout",
    emoji: "✦",
    tag: "push",
    desc: `Imported · ${exercises_list.length} ex · ${parsed.circuits || 3} rounds`,
    circuits: parsed.circuits || 3,
    blockLabels,
    exercises_list,
    cardio,
    importedAt: Date.now(),
    importedFrom: "text",
  };

  return { routine, suggestions, finisher: parsed?.finisher || null };
}

// Convenience helper for the UI — runs both phases and a single
// readiness-aware lookup. Caller supplies the history fetcher + the
// current readiness classification + the full readinessInputs object
// (sleep, HRV trends, muscle recency, fuel) so the algorithm can
// apply specific fatigue overrides beyond the classification.
export async function importWorkoutFromText(text, getHistory, readiness, readinessInputs) {
  const parsed = await parseWorkoutText(text);
  return populateWorkout(parsed, getHistory, readiness, readinessInputs);
}
